import { get, isObject, isEmpty, map, isNumber } from 'lodash';
import * as React from 'react';
import { Grid } from '@amzn/awsui-components-react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import METRICS, { DUAL_ALLOCATION_METRICS, getMetricFormatter } from '../../constants/metrics';
import ValueWithLabel from '../../../../../../common/components/value_with_label/ValueWithLabelForSurveysTab';
import { DUAL_ALLOCATION_STUDY_TYPES } from '../../../../../../redux/reducers/studyTypeReducers';

// Utility functions for metrics calculations
const extractSurveyTypeData = data => {
  if (!data?.aggByStatus) return null;

  const { Submitted = 0, InProgress = 0, Created = 0 } = data.aggByStatus;
  const totalSurveys = Submitted + InProgress + Created;

  return {
    submitted: Submitted,
    inProgress: InProgress,
    created: Created,
    total: totalSurveys,
    metrics: data.metrics || {},
  };
};

const calculateWeightedMetrics = surveyTypesData => {
  const totals = {
    submitted: 0,
    surveys: 0,
    weightedDays: 0,
    weightedFTCR: 0,
    weightedSOR: 0,
    completedCount: 0,
  };

  surveyTypesData.forEach(({ submitted, total, metrics }) => {
    totals.submitted += submitted;
    totals.surveys += total;

    // Weight by submitted surveys for completion-based metrics
    if (submitted > 0 && isNumber(metrics.FirstTimeCompletionRate)) {
      totals.weightedFTCR += metrics.FirstTimeCompletionRate * submitted;
    }

    if (
      submitted > 0 &&
      isNumber(metrics.AverageDaysToComplete) &&
      metrics.AverageDaysToComplete > 0
    ) {
      totals.weightedDays += metrics.AverageDaysToComplete * submitted;
      totals.completedCount += submitted;
    }

    // Weight by total surveys for open rate
    if (total > 0 && isNumber(metrics.SurveyOpenRate)) {
      totals.weightedSOR += metrics.SurveyOpenRate * total;
    }
  });

  return {
    CompletionRate: totals.surveys > 0 ? (totals.submitted / totals.surveys) * 100 : 0,
    SurveyOpenRate: totals.surveys > 0 ? totals.weightedSOR / totals.surveys : 0,
    FirstTimeCompletionRate: totals.submitted > 0 ? totals.weightedFTCR / totals.submitted : 0,
    AverageDaysToComplete:
      totals.completedCount > 0 ? totals.weightedDays / totals.completedCount : 0,
  };
};

const calculateDualAllocationMetrics = surveysData => {
  const surveyTypes = surveysData.surveyTypes || {};

  const surveyTypesData = Object.values(surveyTypes)
    .map(extractSurveyTypeData)
    .filter(Boolean);

  if (surveyTypesData.length === 0) {
    return {
      CompletionRate: 0,
      SurveyOpenRate: 0,
      FirstTimeCompletionRate: 0,
      AverageDaysToComplete: 0,
    };
  }

  return calculateWeightedMetrics(surveyTypesData);
};

// Component utility functions
const formatMetric = (value, key, isPercentage) => {
  if (!isNumber(value)) return 'N/A';
  const formatter = getMetricFormatter(key, isPercentage);
  return formatter(value);
};

const isDualAllocationStudy = studyType => DUAL_ALLOCATION_STUDY_TYPES.includes(studyType);

const getMetricsToDisplay = studyType =>
  isDualAllocationStudy(studyType) ? DUAL_ALLOCATION_METRICS : METRICS;

const createGridDefinition = metricsCount => {
  const colSpan = Math.floor(12 / metricsCount);
  const remainder = 12 % metricsCount;
  const grid = Array(metricsCount).fill({ colspan: colSpan });
  if (remainder > 0) grid[metricsCount - 1] = { colspan: colSpan + remainder };
  return grid;
};

const renderMetricItem = (metric, metrics) => {
  const { label, key, isPercentage } = metric;
  return (
    <div className="right-divider" key={key}>
      <ValueWithLabel label={label}>{formatMetric(metrics[key], key, isPercentage)}</ValueWithLabel>
    </div>
  );
};

const getOverview = (surveysStudySummary, studyType) => {
  let metrics = {};
  if (isDualAllocationStudy(studyType)) {
    metrics = calculateDualAllocationMetrics(surveysStudySummary);
  } else {
    const surveyTypes = get(surveysStudySummary, 'surveyTypes', {});
    const surveyTypeKeys = Object.keys(surveyTypes);

    if (surveyTypeKeys.length === 1) {
      metrics = get(surveysStudySummary, ['surveyTypes', surveyTypeKeys[0], 'metrics'], {});
    } else {
      return null;
    }
  }

  if (!isObject(metrics) || isEmpty(metrics)) {
    return null;
  }

  const metricsToShow = getMetricsToDisplay(studyType);
  const metricsCount = Object.keys(metricsToShow).length;
  const gridDefinition = createGridDefinition(metricsCount);

  return (
    <Grid gridDefinition={gridDefinition}>
      {map(metricsToShow, metric => renderMetricItem(metric, metrics))}
    </Grid>
  );
};

// Connected component with memoization for performance
const OverviewComponent = ({ surveysStudySummary, studyType }) =>
  getOverview(surveysStudySummary, studyType);

OverviewComponent.propTypes = {
  surveysStudySummary: PropTypes.object,
  studyType: PropTypes.string,
};

OverviewComponent.defaultProps = {
  surveysStudySummary: {},
  studyType: '',
};

const ConnectedOverview = connect(
  state => ({
    studyType: get(state, 'studyType.studyType'),
  }),
  null,
  null,
  { pure: true }, // Enable shallow comparison for performance
)(React.memo(OverviewComponent));

export default ConnectedOverview;
